import React, { FC, useEffect } from 'react';
import { useInfiniteQuery } from 'react-query';
import { Spinner } from 'src/components/atoms';
import { AuditLogEntry } from 'src/components/atomsLegacy';
import {
  GetCaseAuditEntriesDocument,
  GetCaseAuditEntriesQuery,
  GetCaseAuditEntriesQueryVariables,
} from 'src/graphql';
import {
  AuditActions,
  caseAuditActions,
  getCaseReadableId,
  getReadableIdWithDash,
  SUBMISSION_MODE,
} from 'src/types';
import { fetcher } from 'src/utils/fetch';
import { AuditLogContainer, LoadingContainer } from './CaseAuditLog.styles';
import { getAuditLogIcon } from './helpers';
import { deduplicateCreateMessageAuditEntries } from './helpers/deduplicateCreateMessageAuditEntries';

interface Props {
  caseId: string;
  disableLegacyStyles?: boolean;
}

export interface AuditEntry {
  DescriptiveText: string;
  action?: string | null;
  id: number;
  createdAt?: string | null;
  data?: { report?: string; submissionMode?: number } | null;
  completed?: boolean | null;
}

const LIMIT = 100;
const SCROLL_BUFFER = 5;

export const CaseAuditLog: FC<Props> = ({ caseId, disableLegacyStyles }) => {
  const { data, isFetching, fetchNextPage } =
    useInfiniteQuery<GetCaseAuditEntriesQuery>({
      queryKey: ['GetCaseAuditEntries', { case: caseId }],
      queryFn: ({ pageParam }) =>
        fetcher<GetCaseAuditEntriesQuery, GetCaseAuditEntriesQueryVariables>(
          GetCaseAuditEntriesDocument,
          {
            case: caseId,
            pagination: { limit: LIMIT, offset: pageParam ?? 0 },
          },
        )(),
      getNextPageParam: (_, allPages) => {
        return allPages.length * LIMIT;
      },
    });

  const auditEntries: AuditEntry[] = (data?.pages ?? []).flatMap(
    (page) => page?.getCaseAuditEntries?.items ?? [],
  );
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const isBottom =
          scrollTop + clientHeight >= scrollHeight - SCROLL_BUFFER;
        const count = data?.pages[0]?.getCaseAuditEntries?.count ?? 0;
        const pageCount = data?.pages.length ?? 0;
        const hasMore = count > pageCount * LIMIT;

        if (isBottom && hasMore && !isFetching) {
          fetchNextPage();
        }
      }
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [data, isFetching, fetchNextPage]);

  const filteredAuditLog = deduplicateCreateMessageAuditEntries(auditEntries);

  return (
    <AuditLogContainer
      $disableLegacyStyles={disableLegacyStyles}
      data-testid="audit-log-container"
      ref={containerRef}
    >
      {filteredAuditLog.map((entry, index) => {
        if (!entry.action) {
          return;
        }

        if (
          entry.action === AuditActions.CREATE_CASE &&
          entry.data?.submissionMode === SUBMISSION_MODE.BULK_IMPORT
        ) {
          return;
        }

        const icon = getAuditLogIcon(entry.action, Boolean(entry.completed));
        let type: 'case' | 'report';
        let id;
        if (caseAuditActions.includes(entry.action as AuditActions)) {
          type = 'case';
          id = getCaseReadableId(caseId);
        } else {
          type = 'report';
          id = getReadableIdWithDash(entry.data?.report);
        }
        return (
          <AuditLogEntry
            auditEntryType={type}
            id={id}
            text={entry.DescriptiveText}
            date={entry.createdAt ?? ''}
            key={`${entry.id}-${index}`}
            icon={icon}
          />
        );
      })}
      {isFetching && (
        <LoadingContainer>
          <Spinner variant="primary" size="large" />
        </LoadingContainer>
      )}
    </AuditLogContainer>
  );
};
